/**
 * Global preset catalogue for `CargoDefinitionVariant`.
 *
 * Each cargo type carries a set of well-known sub-variants the operator
 * can pick from when configuring a definition (e.g. Pallet → Euro,
 * Standard, Double, Half, Chemical). Picking a preset copies its values
 * into the variant on the form; the operator can still tweak or drop
 * back to a fully-custom row.
 *
 * <p>
 * Why hardcoded here (not on the BE): the catalogue is platform-owner
 * curated, evolves rarely, and ships with the FE. Adding a new preset
 * is a deploy, not a data migration. The BE doesn't validate against
 * this list — `presetKey` is just an opaque string from the BE's
 * perspective so the catalogue can evolve without breaking persisted
 * rows.
 * </p>
 *
 * <p>
 * Each variant carries the operationally relevant numbers for its type
 * — Pallet variants have <c>loadingMetres</c>; Iso Tank / Container
 * have <c>perTrailer</c>; Pharma has <c>temperatureBand</c>; Livestock
 * has <c>maxTransitHours</c>. The aggregator types (Full Load / Part
 * Load) have no presets — items come from other definitions at order
 * entry.
 * </p>
 */

import type { CargoTypeValue, RatingUomValue } from './client';

/** Optional inherent physical dimensions some variants carry (custom Iso Tanks, etc.). */
export interface CargoVariantPresetDimensions {
  length?: number;
  width?: number;
  height?: number;
  tareWeight?: number;
}

/** Capacity limits some variants carry (e.g. Iso Tank 40ft → 52m³, 32t). */
export interface CargoVariantPresetCapacity {
  maxWeight?: number;
  maxVolume?: number;
}

export interface CargoVariantPreset {
  /** Stable id sent back to the BE as `presetKey` on save. */
  id: string;
  /** Operator-visible label — copied verbatim into the variant's `name` on save. */
  name: string;

  /** Pallet variants only — trailer space consumed. */
  loadingMetres?: number;
  /** Iso Tank / Container variants only — how many fit on a single trailer. */
  perTrailer?: number;
  /** Livestock variants — regulated max transit hours per species. */
  maxTransitHours?: number;
  /** Pharmaceutical variants — inherent temperature band. */
  temperatureBand?: { min: number; max: number };

  dimensions?: CargoVariantPresetDimensions;
  capacity?: CargoVariantPresetCapacity;

  /** Suggested default UoM when this variant is the active pick at order entry. */
  defaultUom?: RatingUomValue;
}

/**
 * The catalogue. Partial because aggregator types (FullLoad / PartLoad)
 * have no presets — they don't carry variants by design.
 */
export const VARIANTS_BY_TYPE: Partial<Record<CargoTypeValue, CargoVariantPreset[]>> = {
  Pallet: [
    { id: 'euro', name: 'Euro Pallet', loadingMetres: 0.4 },
    { id: 'std', name: 'Standard Pallet', loadingMetres: 0.5 },
    { id: 'double', name: 'Double Pallet', loadingMetres: 0.8 },
    { id: 'half', name: 'Half Pallet', loadingMetres: 0.2 },
    { id: 'chemical', name: 'Chemical Pallet (CP1)', loadingMetres: 0.5 },
  ],
  // PresetKeys are lower_snake_case (with optional dots) to match the
  // BE validator regex — same shape as equipment catalogue keys.
  IsoTank: [
    { id: 'iso_20', name: 'Iso Tank 20ft', perTrailer: 2, defaultUom: 'PerContainer' },
    { id: 'iso_40', name: 'Iso Tank 40ft', perTrailer: 1, defaultUom: 'PerContainer' },
    { id: 'iso_45', name: 'Iso Tank 45ft', perTrailer: 1, defaultUom: 'PerContainer' },
  ],
  Container: [
    { id: 'container_20', name: 'Container 20ft', perTrailer: 2, defaultUom: 'PerContainer' },
    { id: 'container_40', name: 'Container 40ft', perTrailer: 1, defaultUom: 'PerContainer' },
    { id: 'container_45', name: 'Container 45ft', perTrailer: 1, defaultUom: 'PerContainer' },
  ],
  BulkLiquids: [
    { id: 'ibc', name: 'IBC (1000L)', defaultUom: 'PerLitre' },
    { id: 'flexi', name: 'Flexi Tank', defaultUom: 'PerLitre' },
    { id: 'tanker', name: 'Tanker', defaultUom: 'PerLitre' },
  ],
  BulkMaterials: [
    { id: 'wood_chip', name: 'Wood Chip', defaultUom: 'PerTon' },
    { id: 'sand', name: 'Sand', defaultUom: 'PerTon' },
    { id: 'aggregates', name: 'Aggregates', defaultUom: 'PerTon' },
    { id: 'soil', name: 'Soil', defaultUom: 'PerCubicMetre' },
  ],
  AgriGrains: [
    { id: 'wheat', name: 'Feed Wheat', defaultUom: 'PerTon' },
    { id: 'barley', name: 'Barley', defaultUom: 'PerTon' },
    { id: 'maize', name: 'Maize', defaultUom: 'PerTon' },
    { id: 'soya', name: 'Soya / Meal', defaultUom: 'PerTon' },
    { id: 'fertiliser', name: 'Fertiliser', defaultUom: 'PerTon' },
  ],
  Livestock: [
    { id: 'cattle', name: 'Cattle', defaultUom: 'PerHead', maxTransitHours: 14 },
    { id: 'sheep', name: 'Sheep', defaultUom: 'PerHead', maxTransitHours: 14 },
    { id: 'pigs', name: 'Pigs', defaultUom: 'PerHead', maxTransitHours: 24 },
    { id: 'poultry', name: 'Poultry', defaultUom: 'PerHead', maxTransitHours: 12 },
    { id: 'horses', name: 'Horses', defaultUom: 'PerHead', maxTransitHours: 8 },
  ],
  Construction: [
    { id: 'general', name: 'General Construction', defaultUom: 'PerLoad' },
    { id: 'steel', name: 'Steel / Metal', defaultUom: 'PerTon' },
    { id: 'timber', name: 'Timber / Lumber', defaultUom: 'PerLoadingMetre' },
    { id: 'waste', name: 'Waste / Demolition', defaultUom: 'PerLoad' },
    { id: 'machinery', name: 'Machinery / Plant', defaultUom: 'PerLoad' },
    { id: 'prefab', name: 'Prefab / Modular', defaultUom: 'PerLoad' },
  ],
  Pharmaceutical: [
    {
      id: 'gdp_frozen',
      name: 'GDP Frozen (-20°C)',
      temperatureBand: { min: -22, max: -18 },
      defaultUom: 'PerPallet',
    },
    {
      id: 'gdp_refrigerated',
      name: 'GDP Refrigerated (2-8°C)',
      temperatureBand: { min: 2, max: 8 },
      defaultUom: 'PerPallet',
    },
    {
      id: 'gdp_ambient',
      name: 'GDP Controlled Ambient (15-25°C)',
      temperatureBand: { min: 15, max: 25 },
      defaultUom: 'PerPallet',
    },
    {
      id: 'gdp_warm',
      name: 'GDP Warm (15-30°C)',
      temperatureBand: { min: 15, max: 30 },
      defaultUom: 'PerPallet',
    },
  ],
  // FullLoad + PartLoad: no presets — aggregator types have no variants.
};

/** Convenience lookup helper. Returns `[]` for aggregator types. */
export function getVariantPresets(type: CargoTypeValue): CargoVariantPreset[] {
  return VARIANTS_BY_TYPE[type] ?? [];
}

/** Resolve a preset by (type, id) — useful when re-hydrating from BE rows. */
export function findVariantPreset(
  type: CargoTypeValue,
  presetKey: string | null | undefined,
): CargoVariantPreset | undefined {
  if (!presetKey) return undefined;
  return getVariantPresets(type).find((v) => v.id === presetKey);
}
